import {CdkCopyToClipboard} from "@angular/cdk/clipboard";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  RendererStyleFlags2,
  ViewChild
} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {DateRange} from "@angular/material/datepicker";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort, MatSortModule, SortDirection} from "@angular/material/sort";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatTooltipModule} from "@angular/material/tooltip";
import {LoadingService} from "../../../../../../../../nter-ui-kit/src/lib/framework/util/service/loading.service";
import {PageOffering} from "../../../../../core/model/side-nav.model";
import {PanelType} from "../../../../../shared/components/info-panel/info-panel.component";
import {DateUtilsService} from "../../../../../shared/services/dateUtils.service";
import {PaginatorService} from "../../../../../shared/services/paginator.service";
import {UtilsService} from "../../../../../shared/services/utils.service";
import {
  AgreementType,
  LeaseGridSpace,
  LeasePricingPeriod,
  LeaseSummary,
  SharedLeasesSpaceSize
} from "../../../leasing.model";
import {LeaseSubLeasesService} from "../../lease-sub-leases/lease-sub-leases.service";
import {LeaseViewService} from "../../lease-view.service";

@Component({
  selector: 'app-lease-spaces-table',
  standalone: true,
  imports: [
    FlexModule,
    MatTableModule,
    MatPaginator,
    MatTooltipModule,
    MatCheckboxModule,
    CdkCopyToClipboard,
    MatSortModule
  ],
  templateUrl: './lease-spaces-table.component.html',
})
export class LeaseSpacesTableComponent implements OnInit {
  @Input() id!: string;
  @Input() offering!: PageOffering;
  @Input() pricingPeriod?: LeasePricingPeriod;
  @Input() tableStyle: 'custom' | 'dashboard' = 'custom'
  displayedSpaceColumns: string[] = ['id', 'spaceName', 'size', 'leaseStartDate', 'leaseEndDate'];
  displayedSharedSpaceColumns: string[] = ['id', 'occupiedSpace', 'startDate', 'endDate'];
  leaseSpaceDataSource!: MatTableDataSource<LeaseGridSpace>;
  sharedSpacesDataSource!: MatTableDataSource<SharedLeasesSpaceSize>;
  lease?: LeaseSummary;
  parentLease?: LeaseSummary;
  maxRetries = 5;
  retryCount = 0;
  protected readonly PanelType = PanelType;

  @Output() forceDisableEdit = new EventEmitter<boolean>();

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private paginatorService: PaginatorService,
              private leaseViewService: LeaseViewService,
              private subLeasesService: LeaseSubLeasesService,
              public loader: LoadingService,
              private el: ElementRef,
              private renderer: Renderer2) {
  }

  @ViewChild('paginator') set paginator(paginator: MatPaginator) {
    if (paginator) paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    if (this.leaseSpaceDataSource) this.leaseSpaceDataSource.paginator = paginator;
    if (this.sharedSpacesDataSource) this.sharedSpacesDataSource.paginator = paginator;
  }

  @ViewChild(MatSort) set sort(sort: MatSort) {
    if (sort && this.leaseSpaceDataSource) {
      this.leaseSpaceDataSource.sort = sort;
      this.setDefaultSort(sort, 'leaseStartDate', 'asc');
    }
    if (sort && this.sharedSpacesDataSource) {
      this.sharedSpacesDataSource.sort = sort;
      this.setDefaultSort(sort, 'startDate', 'asc');
    }
  }

  get dataSource(): MatTableDataSource<any> | null {
    if (this.leaseSpaceDataSource) return this.leaseSpaceDataSource;
    if (this.sharedSpacesDataSource) return this.sharedSpacesDataSource;
    return null;
  }

  get isSubLease() {
    if (this.lease) return this.lease.agreementType == AgreementType.SUB_LEASE;
    return false;
  }

  ngOnInit() {
    this.loadLease();
  }

  styleTableHeaders() {
    const thElements = this.el.nativeElement.querySelectorAll('th');

    if (thElements.length > 0) {
      // Elements found! Apply styles.
      thElements.forEach((th: HTMLElement) => {
        this.renderer.setStyle(
          th,
          'color',
          '#000000FF',
          RendererStyleFlags2.Important
        );
      });
    } else if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      setTimeout(() => this.styleTableHeaders(), 200);
    } else {
      console.warn('Could not find <th> elements after maximum retries.');
    }
  }

  loadLease() {
    this.leaseViewService.getOne(``, this.id).subscribe({
      next: value => {
        this.lease = value;
        this.loadSpaces();
        if (this.isSubLease) this.loadParentLease();
      },
      error: err => console.log(err)
    })
  }

  loadParentLease() {
    this.leaseViewService.getOne(``, this.lease!.parentUuid!).subscribe({
      next: value => {
        this.parentLease = value;
      },
      error: err => console.log(err)
    })
  }

  loadSpaces() {
    if (!this.isSubLease) {
      this.leaseViewService.getLeaseSpaces(this.id).subscribe({
        next: value => {
          let lgs = [...value];
          if (this.pricingPeriod) {
            this.assignSpacesToPricingPeriod(lgs);
          } else {
            this.leaseSpaceDataSource = new MatTableDataSource<LeaseGridSpace>(lgs);
          }
          if (this.tableStyle == 'dashboard') this.styleTableHeaders();
        },
        error: err => console.log(err)
      })
    }
    if (this.isSubLease) {
      this.subLeasesService.getSharedSpaces(this.id).subscribe({
        next: value => {
          this.sharedSpacesDataSource = new MatTableDataSource<SharedLeasesSpaceSize>(value);
          if (this.tableStyle == 'dashboard') this.styleTableHeaders();
        },
        error: err => console.log(err)
      })
    }
  }

  assignSpacesToPricingPeriod(allSpaces: LeaseGridSpace[]) {
    const pricingRange = new DateRange(new Date(this.pricingPeriod?.startDate * 1000), this.pricingPeriod?.endDate ? new Date(this.pricingPeriod?.endDate * 1000) : null);
    // assign spaces with linked pricing period id in config
    const linkedLgs = allSpaces.filter(f => f.bookingSpaceConfig && f.bookingSpaceConfig?.pricingPeriodUuid == this.pricingPeriod?.uuid);
    this.leaseSpaceDataSource = new MatTableDataSource<LeaseGridSpace>(linkedLgs);

    // assign spaces with no linked id overlap with date range
    const unlinkedLgs = allSpaces.filter(f => !f.bookingSpaceConfig)
      .filter(f => {
        const spaceRange = new DateRange(new Date(f.leaseStartDate), new Date(f.leaseEndDate));
        return this.dateUtils.doesOverlap(pricingRange, spaceRange);
      })

    if (unlinkedLgs.length > 0) {
      this.leaseSpaceDataSource.data = [...this.leaseSpaceDataSource.data, ...unlinkedLgs];
      this.forceDisableEdit.emit(true);
    }
  }

  setDefaultSort(sort: MatSort, active: string, direction?: SortDirection) {
    sort.active = active;
    sort.direction = direction ?? 'asc';
    sort.sortChange.emit({active: sort.active, direction: sort.direction});
  }
}
